import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaChevronDown, FaGlobe } from 'react-icons/fa';
import { useI18n } from '../i18n/I18nContext';

const LanguageSelector = () => {
  const { locale, localeDefinition, locales, setLocale, t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const requestedFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !requestedFocusRef.current) return;

    const options = menuRef.current?.querySelectorAll('[role="menuitemradio"]');
    const index = requestedFocusRef.current === 'last' ? options?.length - 1 : 0;
    options?.[index]?.focus();
    requestedFocusRef.current = null;
  }, [isOpen]);

  const selectLocale = (nextLocale) => {
    setLocale(nextLocale);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const options = [...event.currentTarget.querySelectorAll('[role="menuitemradio"]')];
    const currentIndex = options.indexOf(document.activeElement);
    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1 + options.length) % options.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
    options[nextIndex]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    requestedFocusRef.current = event.key === 'ArrowUp' ? 'last' : 'first';
    setIsOpen(true);
  };

  return (
    <div className="language-selector" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="language-trigger"
        aria-label={t('language.current', { language: localeDefinition.label })}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={handleTriggerKeyDown}
      >
        <FaGlobe aria-hidden="true" />
        <span>{localeDefinition.shortLabel}</span>
        <FaChevronDown className="language-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="language-menu"
          ref={menuRef}
          role="menu"
          aria-label={t('language.select')}
          onKeyDown={handleMenuKeyDown}
        >
          <div className="language-menu-title" role="presentation">{t('language.select')}</div>
          {locales.map((option) => (
            <button
              key={option.code}
              type="button"
              role="menuitemradio"
              aria-checked={locale === option.code}
              className="language-option"
              onClick={() => selectLocale(option.code)}
            >
              <span lang={option.code}>{option.label}</span>
              {locale === option.code && <FaCheck aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
